import React, { useCallback, useEffect, useRef, useState } from 'react';
import researchService from '../../services/researchService';
import youtubeService from '../../services/youtubeService';
import xService from '../../services/xService';
import SecMetricPickerDialog from './SecMetricPickerDialog';

const X_BACKFILL_STATUS = {
  queued: 'Queued',
  processing: 'Processing',
  processed: 'Processed',
  failed: 'Failed',
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).split('T')[0] || String(value);
  return d.toISOString().split('T')[0];
};

const backfillWindowLabel = (years, endDate = new Date()) => {
  const safeYears = Number(years);
  if (!Number.isFinite(safeYears) || safeYears <= 0) return '—';
  const end = new Date(endDate);
  const start = new Date(end);
  start.setFullYear(start.getFullYear() - safeYears);
  return `${formatDate(start)} to ${formatDate(end)}`;
};

const normalizeBackfillStatus = (raw) => {
  const key = String(raw || '').trim().toLowerCase();
  if (key === 'queued') return X_BACKFILL_STATUS.queued;
  if (key === 'processing') return X_BACKFILL_STATUS.processing;
  if (key === 'processed') return X_BACKFILL_STATUS.processed;
  if (key === 'failed') return X_BACKFILL_STATUS.failed;
  return 'Idle';
};

const statusBadgeClass = (status) => {
  const key = String(status || '').toLowerCase();
  if (key === 'processed') return 'bg-success';
  if (key === 'processing') return 'bg-primary';
  if (key === 'queued') return 'bg-warning text-dark';
  if (key === 'failed') return 'bg-danger';
  return 'bg-secondary';
};

const fileStatusBadgeClass = (status) => {
  const map = {
    in_knowledgebase: 'bg-success',
    earnings_extracted: 'bg-primary',
    earnings_confirmed_no_changes: 'bg-primary',
    extraction_error: 'bg-danger',
    extraction_skipped: 'bg-warning text-dark',
    uploading: 'bg-secondary',
    stored_no_kb: 'bg-dark',
  };
  return map[status] || 'bg-secondary';
};

const isTranscriptFile = (file) => {
  const docType = String(file?.doc_type || '').toLowerCase();
  const key = String(file?.s3_key || '').toLowerCase();
  return docType.includes('transcript') || key.includes('/transcripts/');
};

const isXPostFile = (file) => {
  const docType = String(file?.doc_type || '').toLowerCase();
  const key = String(file?.s3_key || '').toLowerCase();
  return (
    docType === 'post-offical' ||
    docType === 'post-official' ||
    docType === 'post-trusted' ||
    key.includes('/x-posts/')
  );
};

const byNewestUploaded = (a, b) => {
  const ta = new Date(a?.uploaded_at || a?.created_at || 0).getTime() || 0;
  const tb = new Date(b?.uploaded_at || b?.created_at || 0).getTime() || 0;
  return tb - ta;
};

const renderSourceFileTable = (items) => (
  <div className="table-responsive" style={{ maxHeight: 260, overflowY: 'auto' }}>
    <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.82rem' }}>
      <thead className="table-light sticky-top">
        <tr>
          <th style={{ width: '40%' }}>File / Title</th>
          <th style={{ width: '16%' }}>Doc Type</th>
          <th style={{ width: '18%' }}>Status</th>
          <th style={{ width: '16%' }}>Doc Date</th>
          <th style={{ width: '18%' }}>Uploaded</th>
        </tr>
      </thead>
      <tbody>
        {items.length === 0 ? (
          <tr>
            <td colSpan={5} className="text-muted fst-italic text-center py-3">
              No files found for this source yet.
            </td>
          </tr>
        ) : items.map((f) => (
          <tr key={f.file_id}>
            <td>
              <div className="fw-semibold">{f.title || f.filename || 'Untitled'}</div>
              {f.title && <div className="text-muted small">{f.filename}</div>}
            </td>
            <td className="text-muted">{f.doc_type || '—'}</td>
            <td>
              <span className={`badge ${fileStatusBadgeClass(f.status)}`}>
                {(f.status || 'unknown').replace(/_/g, ' ')}
              </span>
            </td>
            <td>{f.document_date || '—'}</td>
            <td className="text-muted">{formatDateTime(f.uploaded_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * Modal dialog for configuring a company's research settings.
 *
 * Sections:
 *   1. Basic info  — rename, update ticker
 *   2. Tracked metrics — user-defined list of metric keys to extract each quarter
 *      - Select an uploaded file → "Suggest with AI" → approve / tweak / remove
 *      - Manual add
 *   3. Backfill — re-extract tracked metrics from all existing earnings files
 *
 * Props:
 *   company     { company_id, name, ticker }
 *   files       array of { file_id, filename, doc_type }  (for the file picker)
 *   onSaved(updatedCompany)  — called after PATCH succeeds
 *   onClose()
 */
export default function CompanyConfigDialog({ company, files, onSaved, onClose }) {
  const [sourceFiles, setSourceFiles] = useState(files || []);

  // ── Basic info ──────────────────────────────────────────────────────────
  const [name, setName] = useState(company?.name || '');
  const [ticker, setTicker] = useState(company?.ticker || '');
  const [youtubeChannelId, setYoutubeChannelId] = useState('');
  const [youtubeChannelName, setYoutubeChannelName] = useState('');
  const [youtubeSubscriptionId, setYoutubeSubscriptionId] = useState(null);
  const [xOfficialAccounts, setXOfficialAccounts] = useState('');
  const [xTrustedAccounts, setXTrustedAccounts] = useState('');
  const [xTrustedRequiredKeywords, setXTrustedRequiredKeywords] = useState('');
  const [fiscalYearEndMonth, setFiscalYearEndMonth] = useState('');
  const [fiscalYearEndDay, setFiscalYearEndDay] = useState('');
  const [secPollingEnabled, setSecPollingEnabled] = useState(false);
  const [secLastPollTime, setSecLastPollTime] = useState(null);
  const [secPolling, setSecPolling] = useState(false);
  const [secPollResult, setSecPollResult] = useState(null);
  const [secPollError, setSecPollError] = useState(null);

  // ── Tracked metrics ─────────────────────────────────────────────────────
  // Each metric: { key, label, description, _id } — _id is UI-only
  const [metrics, setMetrics] = useState([]);
  const [loadingConfig, setLoadingConfig] = useState(true);

  // AI suggest state
  const [suggestFileId, setSuggestFileId] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState(null);
  const [suggestedMetrics, setSuggestedMetrics] = useState([]); // pending — not yet added to tracked list

  // New-metric inline form
  const [newKey, setNewKey] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newDesc, setNewDesc] = useState('');

  // Save state
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveWarning, setSaveWarning] = useState(null);

  // Backfill state
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [backfillUnit, setBackfillUnit] = useState('quarters');
  const [backfillCount, setBackfillCount] = useState(8);
  const [backfillIncludeSec, setBackfillIncludeSec] = useState(false);
  const [youtubeBackfilling, setYoutubeBackfilling] = useState(false);
  const [youtubeBackfillYears, setYoutubeBackfillYears] = useState(2);
  const [youtubeBackfillResult, setYoutubeBackfillResult] = useState(null);
  const [youtubeBackfillError, setYoutubeBackfillError] = useState(null);
  const [youtubeBackfillStartedAt, setYoutubeBackfillStartedAt] = useState(null);
  const [youtubeTranscriptBaselineCount, setYoutubeTranscriptBaselineCount] = useState(0);
  const [xBackfilling, setXBackfilling] = useState(false);
  const [xBackfillYears, setXBackfillYears] = useState(2);
  const [xBackfillResult, setXBackfillResult] = useState(null);
  const [xBackfillError, setXBackfillError] = useState(null);
  const [xBackfillStartedAt, setXBackfillStartedAt] = useState(null);
  const [xResearchAccounts, setXResearchAccounts] = useState([]);
  const [xAccountsLoading, setXAccountsLoading] = useState(false);

  // SEC metric picker modal
  const [showSecPicker, setShowSecPicker] = useState(false);

  // Counter for stable UI keys on new metrics
  const nextId = useRef(0);
  const mkId = () => `m_${nextId.current++}`;

  const transcriptFiles = sourceFiles.filter(isTranscriptFile).sort(byNewestUploaded);
  const xPostFiles = sourceFiles.filter(isXPostFile).sort(byNewestUploaded);

  const refreshSourceFiles = useCallback(async () => {
    if (!company?.company_id) return;
    try {
      const latest = await researchService.listFiles(company.company_id);
      setSourceFiles(latest || []);
    } catch (err) {
      console.warn('Could not refresh source files for config dialog', err);
    }
  }, [company?.company_id]);

  const loadXResearchAccounts = useCallback(async () => {
    if (!company?.company_id) return;
    setXAccountsLoading(true);
    try {
      const xCfg = await xService.getResearchAccounts(company.company_id);
      setXResearchAccounts(xCfg?.accounts || []);
      setXTrustedRequiredKeywords((xCfg?.trusted_required_keywords || []).join('\n'));
    } catch (err) {
      console.warn('Could not load X research account filters', err);
      setXResearchAccounts([]);
    } finally {
      setXAccountsLoading(false);
    }
  }, [company?.company_id]);

  // ── Load existing config on mount ───────────────────────────────────────
  useEffect(() => {
    if (!company?.company_id) return;
    setLoadingConfig(true);
    researchService
      .getCompanyConfig(company.company_id)
      .then((cfg) => {
        setName(cfg.name || '');
        setTicker(cfg.ticker || '');
        setYoutubeChannelId(cfg.youtube_channel_id || '');
        setYoutubeChannelName(cfg.youtube_channel_name || '');
        setYoutubeSubscriptionId(cfg.youtube_subscription_id || null);
        setXOfficialAccounts((cfg.x_official_accounts || []).join('\n'));
        setXTrustedAccounts((cfg.x_trusted_accounts || []).join('\n'));
        loadXResearchAccounts();
        setFiscalYearEndMonth(
          cfg.fiscal_year_end_month ? String(cfg.fiscal_year_end_month) : '',
        );
        setFiscalYearEndDay(
          cfg.fiscal_year_end_day ? String(cfg.fiscal_year_end_day) : '',
        );
        setSecPollingEnabled(!!cfg.sec_polling_enabled);
        setSecLastPollTime(cfg.sec_last_poll_time || null);
        setMetrics(
          (cfg.tracked_metrics || []).map((m) => ({ ...m, _id: mkId() })),
        );
      })
      .catch((err) => {
        // Non-fatal: just start with empty list
        console.warn('Could not load company config', err);
      })
      .finally(() => setLoadingConfig(false));
  }, [company?.company_id, loadXResearchAccounts]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSourceFiles(files || []);
  }, [files]);

  useEffect(() => {
    if (!company?.company_id) return;
    refreshSourceFiles();
  }, [company?.company_id, refreshSourceFiles]);

  useEffect(() => {
    if (!company?.company_id) return;
    const hasActiveWatch = !!youtubeBackfillResult || !!xBackfillResult;
    if (!hasActiveWatch) return;
    const id = setInterval(() => {
      refreshSourceFiles();
      loadXResearchAccounts();
    }, 12000);
    return () => clearInterval(id);
  }, [company?.company_id, youtubeBackfillResult, xBackfillResult, refreshSourceFiles, loadXResearchAccounts]);

  // ── Metrics helpers ──────────────────────────────────────────────────────
  const updateMetric = useCallback((id, field, value) => {
    setMetrics((prev) =>
      prev.map((m) => (m._id === id ? { ...m, [field]: value } : m)),
    );
  }, []);

  const removeMetric = useCallback((id) => {
    setMetrics((prev) => prev.filter((m) => m._id !== id));
  }, []);

  const addMetric = () => {
    const key = newKey.trim().replace(/\s+/g, '_').toLowerCase();
    if (!key) return;
    setMetrics((prev) => [
      ...prev,
      { key, label: newLabel.trim(), description: newDesc.trim(), _id: mkId() },
    ]);
    setNewKey('');
    setNewLabel('');
    setNewDesc('');
  };

  // ── AI suggestion ────────────────────────────────────────────────────────
  const handleSuggest = async () => {
    if (!suggestFileId) return;
    setSuggestError(null);
    setSuggesting(true);
    setSuggestedMetrics([]);
    try {
      const result = await researchService.suggestMetrics(
        company.company_id,
        suggestFileId,
      );
      // Show suggestions in separate review section — don't auto-merge
      const existing = new Set(metrics.map((m) => m.key));
      const fresh = (result.suggested_metrics || [])
        .filter((s) => s.key && !existing.has(s.key))
        .map((s) => ({ ...s, _id: mkId() }));
      setSuggestedMetrics(fresh);
    } catch (err) {
      setSuggestError(
        err?.response?.data?.detail || err.message || 'Suggestion failed',
      );
    } finally {
      setSuggesting(false);
    }
  };

  const acceptSuggestion = useCallback((id) => {
    setSuggestedMetrics((prev) => {
      const m = prev.find((s) => s._id === id);
      if (m) {
        setMetrics((ms) => {
          if (ms.some((x) => x.key === m.key)) return ms;
          return [...ms, m];
        });
      }
      return prev.filter((s) => s._id !== id);
    });
  }, []);

  const acceptAllSuggestions = useCallback(() => {
    setSuggestedMetrics((prev) => {
      setMetrics((ms) => {
        const existingKeys = new Set(ms.map((x) => x.key));
        return [...ms, ...prev.filter((m) => !existingKeys.has(m.key))];
      });
      return [];
    });
  }, []);

  const dismissSuggestion = useCallback((id) => {
    setSuggestedMetrics((prev) => prev.filter((s) => s._id !== id));
  }, []);

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaveError(null);
    setSaveWarning(null);
    setSaving(true);
    try {
      const month = fiscalYearEndMonth ? Number(fiscalYearEndMonth) : null;
      const day = fiscalYearEndDay ? Number(fiscalYearEndDay) : null;

      if ((month === null) !== (day === null)) {
        setSaveError('Fiscal year-end month and day must both be set.');
        setSaving(false);
        return;
      }
      if (month !== null || day !== null) {
        const validMonth = Number.isInteger(month) && month >= 1 && month <= 12;
        const validDay = Number.isInteger(day) && day >= 1 && day <= 31;
        if (!validMonth || !validDay) {
          setSaveError('Fiscal year-end month/day must be valid numbers (month 1–12, day 1–31).');
          setSaving(false);
          return;
        }
      }

      const cleanMetrics = metrics
        .filter((m) => m.key)
        .map(({ key, label, description, xbrl_tag, taxonomy }) => ({
          key,
          ...(label ? { label } : {}),
          ...(description ? { description } : {}),
          ...(xbrl_tag ? { xbrl_tag } : {}),
          ...(taxonomy ? { taxonomy } : {}),
        }));

      const trimmedYoutubeChannelId = youtubeChannelId.trim();
      const resolvedYoutubeChannelName =
        youtubeChannelName.trim() || name.trim() || company?.name || '';
      const youtubeChannelUrl = trimmedYoutubeChannelId
        ? `https://www.youtube.com/channel/${trimmedYoutubeChannelId}`
        : null;

      let nextYoutubeSubscriptionId = youtubeSubscriptionId;
      let existingSubscription = null;
      let youtubeSyncWarning = null;

      if (trimmedYoutubeChannelId && !trimmedYoutubeChannelId.startsWith('UC')) {
        setSaveError('YouTube channel ID must start with "UC".');
        setSaving(false);
        return;
      }

      // YouTube subscription sync is best-effort: if the YouTube service is
      // unavailable (e.g. not running locally), don't let it block saving the
      // rest of the company config (name, ticker, X accounts, metrics, etc.).
      try {
        if (nextYoutubeSubscriptionId) {
          try {
            const subResult = await youtubeService.getSubscription(nextYoutubeSubscriptionId);
            existingSubscription = subResult?.subscription || null;
          } catch (err) {
            console.warn('Could not load existing YouTube subscription by id', err);
            nextYoutubeSubscriptionId = null;
          }
        }

        if (!existingSubscription) {
          try {
            const allSubs = await youtubeService.getAllSubscriptions();
            existingSubscription =
              (allSubs?.subscriptions || []).find((s) => s.company_id === company.company_id) || null;
            if (existingSubscription?.id) nextYoutubeSubscriptionId = existingSubscription.id;
          } catch (err) {
            console.warn('Could not list YouTube subscriptions for company lookup', err);
          }
        }

        if (trimmedYoutubeChannelId) {
          if (existingSubscription) {
            if (existingSubscription.channel_id !== trimmedYoutubeChannelId) {
              await youtubeService.deleteSubscription(existingSubscription.id);
              existingSubscription = null;
              nextYoutubeSubscriptionId = null;
            }
          }

          if (!existingSubscription) {
            const created = await youtubeService.createSubscription({
              channel_id: trimmedYoutubeChannelId,
              channel_name: resolvedYoutubeChannelName,
              company_id: company.company_id,
              subscription_type: 'Research',
              teams_channel_name: '',
              teams_webhook_url: '',
              enable_teams_notifications: false,
              notify_all: false,
            });
            nextYoutubeSubscriptionId = created?.subscription?.id || null;
          } else {
            await youtubeService.updateSubscription(existingSubscription.id, {
              channel_name: resolvedYoutubeChannelName,
              subscription_type: 'Research',
              enable_teams_notifications: false,
            });
            nextYoutubeSubscriptionId = existingSubscription.id;
          }
        } else if (existingSubscription) {
          await youtubeService.deleteSubscription(existingSubscription.id);
          nextYoutubeSubscriptionId = null;
        }
      } catch (err) {
        console.warn('Could not sync YouTube subscription (is the YouTube service running?)', err);
        if (trimmedYoutubeChannelId) nextYoutubeSubscriptionId = youtubeSubscriptionId;
        youtubeSyncWarning =
          'YouTube subscription could not be synced (service unavailable) — other settings were still saved.';
      }

      const updated = await researchService.updateCompany(company.company_id, {
        name: name.trim() || undefined,
        ticker: ticker.trim() || undefined,
        youtube_channel_id: trimmedYoutubeChannelId || null,
        youtube_channel_name: trimmedYoutubeChannelId ? resolvedYoutubeChannelName : null,
        youtube_channel_url: youtubeChannelUrl,
        youtube_subscription_id: nextYoutubeSubscriptionId || null,
        x_official_accounts: parseHandles(xOfficialAccounts),
        x_trusted_accounts: parseHandles(xTrustedAccounts),
        fiscal_year_end_month: month,
        fiscal_year_end_day: day,
        tracked_metrics: cleanMetrics,
        sec_polling_enabled: secPollingEnabled,
      });

      // Register/replace monitored X accounts (source of truth for monitoring
      // + backfill). Non-fatal on failure so config still saves.
      let xSyncWarning = null;
      try {
        await xService.syncResearchAccounts(company.company_id, {
          officialHandles: parseHandles(xOfficialAccounts),
          trustedHandles: parseHandles(xTrustedAccounts),
          trustedRequiredKeywords: parseKeywords(xTrustedRequiredKeywords),
        });
      } catch (err) {
        console.warn('Could not sync monitored X accounts (is the X service running?)', err);
        xSyncWarning =
          'Monitored X accounts could not be synced (service unavailable) — other settings were still saved.';
      }

      const combinedWarning = [youtubeSyncWarning, xSyncWarning].filter(Boolean).join(' ');
      setSaveWarning(combinedWarning || null);

      if (onSaved) onSaved(updated);
      if (!combinedWarning) onClose();
    } catch (err) {
      setSaveError(err?.response?.data?.detail || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── SEC filing polling (manual "poll now") ─────────────────────────────────
  const handleSecPollNow = async () => {
    if (!company?.company_id || secPolling) return;
    setSecPolling(true);
    setSecPollResult(null);
    setSecPollError(null);
    try {
      // Persist toggle first so backend guard passes even if user has not
      // clicked the main Save button yet.
      await researchService.updateCompany(company.company_id, {
        sec_polling_enabled: true,
      });
      const data = await researchService.pollSecFilings(company.company_id);
      setSecPollResult(data);
      setSecLastPollTime(new Date().toISOString());
    } catch (err) {
      setSecPollError(
        err?.response?.data?.detail ||
        err.message ||
        'SEC poll failed',
      );
    } finally {
      setSecPolling(false);
    }
  };

  // ── Backfill ──────────────────────────────────────────────────────────────
  const handleBackfill = async () => {
    setBackfillResult(null);
    setBackfilling(true);
    try {
      const hasTicker = !!company?.ticker;
      const apiOpts = { includeSecCustomMetrics: backfillIncludeSec };
      if (backfillUnit === 'years') apiOpts.years = Number(backfillCount);
      else apiOpts.quarters = Number(backfillCount);

      const promises = [
        researchService.backfillMetrics(company.company_id),
        hasTicker
          ? researchService.apiBackfill(company.company_id, apiOpts)
          : Promise.resolve(null),
      ];
      const [fileSettled, apiSettled] = await Promise.allSettled(promises);

      const fileRes =
        fileSettled.status === 'fulfilled'
          ? fileSettled.value
          : {
              success: false,
              periods_updated: [],
              errors: [
                fileSettled.reason?.response?.data?.detail ||
                  fileSettled.reason?.message ||
                  'File backfill failed',
              ],
            };
      const apiRes =
        !hasTicker
          ? null
          : apiSettled.status === 'fulfilled'
          ? apiSettled.value
          : {
              success: false,
              periods_written: [],
              errors: [
                apiSettled.reason?.response?.data?.detail ||
                  apiSettled.reason?.message ||
                  'SEC API backfill failed',
              ],
            };
      setBackfillResult({ file: fileRes, api: apiRes });
    } catch (err) {
      setBackfillResult({
        file: { success: false, periods_updated: [], errors: [err.message || 'Backfill failed'] },
        api: null,
      });
    } finally {
      setBackfilling(false);
    }
  };

  const handleYoutubeBackfill = async () => {
    setYoutubeBackfillError(null);
    setYoutubeBackfillResult(null);
    setYoutubeBackfilling(true);
    try {
      const years = Number(youtubeBackfillYears);
      if (!Number.isInteger(years) || years < 1 || years > 20) {
        throw new Error('Backfill years must be an integer from 1 to 20.');
      }

      let subscriptionId = youtubeSubscriptionId;
      if (!subscriptionId) {
        const allSubs = await youtubeService.getAllSubscriptions();
        const matching =
          (allSubs?.subscriptions || []).find((s) => s.company_id === company.company_id) ||
          null;
        if (matching?.id) {
          subscriptionId = matching.id;
          setYoutubeSubscriptionId(matching.id);
        }
      }

      if (!subscriptionId) {
        throw new Error('No YouTube subscription found. Save the channel first, then run backfill.');
      }

      const result = await youtubeService.backfillTranscripts(subscriptionId, { years });
      setYoutubeBackfillStartedAt(new Date().toISOString());
      setYoutubeTranscriptBaselineCount(transcriptFiles.length);
      setYoutubeBackfillResult(result || null);
      refreshSourceFiles();
    } catch (err) {
      setYoutubeBackfillError(
        err?.response?.data?.detail || err?.message || 'YouTube transcript backfill failed',
      );
    } finally {
      setYoutubeBackfilling(false);
    }
  };

  // Parse a newline/comma-separated list of X handles into a clean array.
  const parseHandles = (raw) =>
    (raw || '')
      .split(/[\n,]+/)
      .map((h) => h.trim().replace(/^@+/, ''))
      .filter(Boolean);

  const parseKeywords = (raw) =>
    (raw || '')
      .split(/[\n,]+/)
      .map((k) => k.trim().toLowerCase())
      .filter(Boolean);

  const handleXBackfill = async () => {
    setXBackfillError(null);
    setXBackfillResult(null);
    setXBackfilling(true);
    try {
      const years = Number(xBackfillYears);
      if (!Number.isInteger(years) || years < 1 || years > 20) {
        throw new Error('Backfill years must be an integer from 1 to 20.');
      }
      const official = parseHandles(xOfficialAccounts);
      const trusted = parseHandles(xTrustedAccounts);
      if (official.length === 0 && trusted.length === 0) {
        throw new Error('Add at least one Official or Trusted X account, then Save before backfilling.');
      }
      // Ensure monitored accounts are registered before queueing backfill.
      await xService.syncResearchAccounts(company.company_id, {
        officialHandles: official,
        trustedHandles: trusted,
        trustedRequiredKeywords: parseKeywords(xTrustedRequiredKeywords),
      });
      const result = await xService.backfillResearchPosts(company.company_id, { years });
      setXBackfillStartedAt(new Date().toISOString());
      setXBackfillResult(result || null);
      loadXResearchAccounts();
      refreshSourceFiles();
    } catch (err) {
      setXBackfillError(
        err?.response?.data?.detail || err?.message || 'X post backfill failed',
      );
    } finally {
      setXBackfilling(false);
    }
  };

  // ── Earnings files only (for the AI picker) ───────────────────────────────
  // Legacy records were stored with doc_type='notes' even when earnings; infer from s3_key/status.
  const isEarningsFile = (f) => {
    if (f.doc_type === 'earnings_press_release' || f.doc_type === 'earnings_deck') return true;
    if (f.s3_key && f.s3_key.includes('/earnings/')) return true;
    if (f.status && f.status.startsWith('earnings_')) return true;
    return false;
  };
  const earningsFiles = (files || []).filter(isEarningsFile);

  const xBackfillCounts = xResearchAccounts.reduce(
    (acc, account) => {
      const s = normalizeBackfillStatus(account?.backfill_status).toLowerCase();
      if (s === 'queued') acc.queued += 1;
      else if (s === 'processing') acc.processing += 1;
      else if (s === 'processed') acc.processed += 1;
      else if (s === 'failed') acc.failed += 1;
      else acc.idle += 1;
      return acc;
    },
    { queued: 0, processing: 0, processed: 0, failed: 0, idle: 0 },
  );

  const xProgressTotal =
    (xBackfillResult?.queued || 0) > 0
      ? xBackfillResult.queued
      : xBackfillCounts.queued + xBackfillCounts.processing + xBackfillCounts.processed + xBackfillCounts.failed;
  const xProgressDone = xBackfillCounts.processed + xBackfillCounts.failed;
  const xProgressPct = xProgressTotal > 0 ? Math.round((xProgressDone / xProgressTotal) * 100) : 0;

  const youtubeQueuedTarget = youtubeBackfillResult?.queued_videos_count ?? youtubeBackfillResult?.queued ?? 0;
  const observedTranscriptDelta = Math.max(0, transcriptFiles.length - youtubeTranscriptBaselineCount);
  const youtubeProgressPct = youtubeQueuedTarget > 0
    ? Math.min(100, Math.round((observedTranscriptDelta / youtubeQueuedTarget) * 100))
    : 0;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1055 }} />
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        style={{ zIndex: 1060, overflowY: 'auto' }}
      >
        <div
          className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
          role="document"
        >
          <div className="modal-content">
            {/* Header */}
            <div className="modal-header bg-dark text-white">
              <h5 className="modal-title">
                <i className="bi bi-gear me-2" />
                Configure: {company?.name}
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onClose}
                disabled={saving}
                aria-label="Close"
              />
            </div>

            <div className="modal-body">
              {loadingConfig ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-secondary" role="status" />
                </div>
              ) : (
                <>
                  {/* ── Section 1: Basic info ── */}
                  <h6 className="fw-semibold text-uppercase text-muted mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                    Basic Info
                  </h6>
                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <label className="form-label">Company name</label>
                      <input
                        className="form-control"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Ticker</label>
                      <input
                        className="form-control text-uppercase"
                        value={ticker}
                        onChange={(e) => setTicker(e.target.value.toUpperCase())}
                        placeholder="e.g. AAPL"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Fiscal Year End (month/day)</label>
                      <div className="d-flex gap-2">
                        <input
                          className="form-control"
                          type="number"
                          min="1"
                          max="12"
                          value={fiscalYearEndMonth}
                          onChange={(e) => setFiscalYearEndMonth(e.target.value)}
                          placeholder="MM"
                        />
                        <input
                          className="form-control"
                          type="number"
                          min="1"
                          max="31"
                          value={fiscalYearEndDay}
                          onChange={(e) => setFiscalYearEndDay(e.target.value)}
                          placeholder="DD"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Official YouTube Channel ID (optional)</label>
                      <input
                        className="form-control"
                        value={youtubeChannelId}
                        onChange={(e) => setYoutubeChannelId(e.target.value.trim())}
                        placeholder="UCxxxxxxxxxxxxxxxxxxxxxxx"
                      />
                      <div className="form-text">
                        When set, new transcripts are saved to hff-research under transcripts/ for KB ingestion.
                        {' '}
                        You can find the official YouTube Channel ID here:{' '}
                        <a
                          href="https://www.streamweasels.com/%20tools/youtube-channel-id-and-%20user-id-convertor/"
                          target="_blank"
                          rel="noreferrer"
                        >
                          streamweasels.com
                        </a>
                        .
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">YouTube Channel Name (optional)</label>
                      <input
                        className="form-control"
                        value={youtubeChannelName}
                        onChange={(e) => setYoutubeChannelName(e.target.value)}
                        placeholder="Defaults to company name"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        <i className="bi bi-twitter-x me-1" />
                        Official X accounts (optional)
                      </label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={xOfficialAccounts}
                        onChange={(e) => setXOfficialAccounts(e.target.value)}
                        placeholder={'One handle per line, e.g.\n@upstart\n@companyIR'}
                      />
                      <div className="form-text">
                        Company-owned accounts Or C-Suite accounts.
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        <i className="bi bi-twitter-x me-1" />
                        Trusted X accounts (optional)
                      </label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={xTrustedAccounts}
                        onChange={(e) => setXTrustedAccounts(e.target.value)}
                        placeholder={'One handle per line, e.g.\n@analyst\n@industryWatcher'}
                      />
                      <div className="form-text">
                        Trusted third-party accounts like news sources or ambassador of the brand.
                      </div>
                    </div>
                    <div className="col-md-6">  </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        Trusted-post required keywords (optional)
                      </label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={xTrustedRequiredKeywords}
                        onChange={(e) => setXTrustedRequiredKeywords(e.target.value)}
                        placeholder={'One keyword per line, e.g.\nupstart\nUPST\nearnings'}
                      />
                      <div className="form-text">
                        Applied only to Trusted accounts. A post must contain at least one keyword to be archived.
                      </div>
                    </div>
                  </div>
                  {fiscalYearEndMonth && fiscalYearEndDay && (
                    Number(fiscalYearEndMonth) !== 12 || Number(fiscalYearEndDay) !== 31
                  ) && (
                    <div className="alert alert-info py-2 px-3 mb-4" role="alert">
                      Fiscal quarters are offset from calendar quarters for this company.
                      Reporting quarter and fiscal year are derived from period-end dates.
                    </div>
                  )}

                  {/* ── Section 2: Tracked metrics ── */}
                  <h6 className="fw-semibold text-uppercase text-muted mb-1" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                    Tracked Metrics
                  </h6>
                  <p className="text-muted small mb-3">
                    These metrics will be extracted from every earnings upload.
                    The LLM will look for exactly these keys, reducing hallucination
                    and key-drift.
                  </p>

                  {/* AI suggest row */}
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <select
                      className="form-select form-select-sm"
                      style={{ maxWidth: 380 }}
                      value={suggestFileId}
                      onChange={(e) => setSuggestFileId(e.target.value)}
                    >
                      <option value="">— pick an earnings file to analyse —</option>
                      {earningsFiles.map((f) => (
                        <option key={f.file_id} value={f.file_id}>
                          {f.title || f.filename}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={handleSuggest}
                      disabled={!suggestFileId || suggesting}
                    >
                      {suggesting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" />
                          Analysing…
                        </>
                      ) : (
                        <>
                          <i className="bi bi-robot me-1" />
                          Suggest with AI
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowSecPicker(true)}
                      disabled={!ticker.trim()}
                      title={
                        ticker.trim()
                          ? 'Browse XBRL tags reported by SEC EDGAR for this ticker'
                          : 'Set a ticker above to enable SEC metric browsing'
                      }
                    >
                      <i className="bi bi-search me-1" />
                      Browse SEC metrics
                    </button>
                    {suggestError && (
                      <span className="text-danger small">{suggestError}</span>
                    )}
                  </div>

                  {/* AI suggestions review section */}
                  {suggestedMetrics.length > 0 && (
                    <div className="border border-primary rounded p-3 mb-3" style={{ background: '#f0f4ff' }}>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fw-semibold" style={{ fontSize: '0.82rem' }}>
                          <i className="bi bi-robot text-primary me-1" />
                          AI Suggestions — review before adding
                        </span>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-primary py-0"
                            onClick={acceptAllSuggestions}
                          >
                            <i className="bi bi-check-all me-1" />Add all
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary py-0"
                            onClick={() => setSuggestedMetrics([])}
                          >
                            Dismiss all
                          </button>
                        </div>
                      </div>
                      <div className="table-responsive" style={{ maxHeight: 260, overflowY: 'auto' }}>
                        <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.82rem' }}>
                          <thead className="table-primary sticky-top">
                            <tr>
                              <th style={{ width: '25%' }}>Key</th>
                              <th style={{ width: '20%' }}>Label</th>
                              <th>Description</th>
                              <th style={{ width: '1%' }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {suggestedMetrics.map((m) => (
                              <tr key={m._id}>
                                <td className="font-monospace">{m.key}</td>
                                <td>{m.label || '—'}</td>
                                <td className="text-muted">{m.description || ''}</td>
                                <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                                  <button
                                    type="button"
                                    className="btn btn-outline-success btn-sm py-0 me-1"
                                    title="Add to tracked metrics"
                                    onClick={() => acceptSuggestion(m._id)}
                                  >
                                    <i className="bi bi-plus" />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm py-0"
                                    title="Dismiss"
                                    onClick={() => dismissSuggestion(m._id)}
                                  >
                                    ✕
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Tracked metrics table */}
                  <div className="table-responsive mb-2" style={{ maxHeight: 320, overflowY: 'auto' }}>
                    <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.82rem' }}>
                      <thead className="table-dark sticky-top">
                        <tr>
                          <th style={{ width: '25%' }}>Key (snake_case)</th>
                          <th style={{ width: '20%' }}>Label</th>
                          <th>Description / extraction hint</th>
                          <th style={{ width: '1%' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {metrics.length === 0 && (
                          <tr>
                            <td colSpan={4} className="text-center text-muted fst-italic py-3">
                              No tracked metrics yet — use AI suggestion or add manually below.
                            </td>
                          </tr>
                        )}
                        {metrics.map((m) => (
                          <tr key={m._id}>
                            <td>
                              <input
                                className="form-control form-control-sm font-monospace"
                                value={m.key}
                                onChange={(e) =>
                                  updateMetric(
                                    m._id,
                                    'key',
                                    e.target.value.replace(/\s+/g, '_').toLowerCase(),
                                  )
                                }
                              />
                              {m.xbrl_tag && (
                                <div
                                  className="text-muted mt-1"
                                  style={{ fontSize: '0.7rem' }}
                                  title={`Bound to SEC XBRL tag ${m.taxonomy || ''}:${m.xbrl_tag}`}
                                >
                                  <i className="bi bi-link-45deg" />
                                  {' '}
                                  <code style={{ fontSize: '0.7rem' }}>
                                    {m.taxonomy ? `${m.taxonomy}:` : ''}{m.xbrl_tag}
                                  </code>
                                </div>
                              )}
                            </td>
                            <td>
                              <input
                                className="form-control form-control-sm"
                                value={m.label || ''}
                                placeholder="e.g. Net Income"
                                onChange={(e) =>
                                  updateMetric(m._id, 'label', e.target.value)
                                }
                              />
                            </td>
                            <td>
                              <input
                                className="form-control form-control-sm"
                                value={m.description || ''}
                                placeholder="Optional hint for the LLM"
                                onChange={(e) =>
                                  updateMetric(m._id, 'description', e.target.value)
                                }
                              />
                            </td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm py-0"
                                title="Remove metric"
                                onClick={() => removeMetric(m._id)}
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        ))}

                        {/* Inline add row */}
                        <tr className="table-light">
                          <td>
                            <input
                              className="form-control form-control-sm font-monospace"
                              placeholder="new_key"
                              value={newKey}
                              onChange={(e) =>
                                setNewKey(e.target.value.replace(/\s+/g, '_').toLowerCase())
                              }
                              onKeyDown={(e) => e.key === 'Enter' && addMetric()}
                            />
                          </td>
                          <td>
                            <input
                              className="form-control form-control-sm"
                              placeholder="Label"
                              value={newLabel}
                              onChange={(e) => setNewLabel(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && addMetric()}
                            />
                          </td>
                          <td>
                            <input
                              className="form-control form-control-sm"
                              placeholder="Description (optional)"
                              value={newDesc}
                              onChange={(e) => setNewDesc(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && addMetric()}
                            />
                          </td>
                          <td className="text-center">
                            <button
                              type="button"
                              className="btn btn-outline-success btn-sm py-0"
                              title="Add metric"
                              onClick={addMetric}
                              disabled={!newKey.trim()}
                            >
                              +
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* ── Section 3: Backfill ── */}
                  <div className="border rounded p-3 mt-4 bg-light">
                    <h6 className="fw-semibold mb-1">
                      <i className="bi bi-arrow-repeat me-2 text-primary" />
                      Backfill
                    </h6>
                    <p className="small text-muted mb-2">
                      Re-extract tracked metrics from every existing earnings file
                      <em> and</em> pull standard financials + custom XBRL metrics
                      from SEC EDGAR — both run in parallel.
                    </p>

                    <div className="d-flex flex-wrap align-items-end gap-3 mb-2">
                      {/* How far back (SEC API only) */}
                      {company?.ticker && (
                        <div>
                          <label className="form-label small mb-1 d-block">SEC: how far back?</label>
                          <div className="input-group input-group-sm" style={{ width: 220 }}>
                            <input
                              type="number"
                              min={1}
                              max={backfillUnit === 'years' ? 20 : 80}
                              className="form-control"
                              value={backfillCount}
                              onChange={(e) => setBackfillCount(e.target.value)}
                              disabled={backfilling}
                            />
                            <select
                              className="form-select"
                              value={backfillUnit}
                              onChange={(e) => setBackfillUnit(e.target.value)}
                              disabled={backfilling}
                            >
                              <option value="quarters">quarters</option>
                              <option value="years">years</option>
                            </select>
                          </div>
                        </div>
                      )}

                      {company?.ticker && (
                        <div className="form-check mb-1">
                          <input
                            type="checkbox"
                            id="cfg-backfill-sec-custom"
                            className="form-check-input"
                            checked={backfillIncludeSec}
                            onChange={(e) => setBackfillIncludeSec(e.target.checked)}
                            disabled={backfilling}
                          />
                          <label className="form-check-label small" htmlFor="cfg-backfill-sec-custom">
                            Include SEC custom metrics
                          </label>
                        </div>
                      )}

                      <button
                        type="button"
                        className="btn btn-sm btn-warning"
                        onClick={handleBackfill}
                        disabled={backfilling || metrics.length === 0}
                      >
                        {backfilling ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" />
                            Backfilling…
                          </>
                        ) : (
                          <>
                            <i className="bi bi-database-gear me-1" />
                            Run backfill
                          </>
                        )}
                      </button>
                    </div>

                    {backfillResult && (
                      <div className="d-flex flex-column gap-2 mt-1">
                        {/* File backfill result */}
                        <div
                          className={`alert py-1 px-2 mb-0 small ${
                            backfillResult.file?.success ? 'alert-success' : 'alert-warning'
                          }`}
                        >
                          <strong>Files:</strong>{' '}
                          {backfillResult.file?.success
                            ? `Updated ${(backfillResult.file.periods_updated || []).length} period(s)${
                                (backfillResult.file.periods_updated || []).length
                                  ? ': ' + backfillResult.file.periods_updated.join(', ')
                                  : ''
                              }`
                            : (backfillResult.file?.errors || []).join('; ') || 'Failed'}
                        </div>

                        {/* SEC API result */}
                        {backfillResult.api && (
                          <div
                            className={`alert py-1 px-2 mb-0 small ${
                              backfillResult.api.success &&
                              !(backfillResult.api.errors || []).length
                                ? 'alert-success'
                                : backfillResult.api.success
                                ? 'alert-warning'
                                : 'alert-danger'
                            }`}
                          >
                            <strong>SEC API:</strong>{' '}
                            {backfillResult.api.success ? (
                              <>
                                Wrote {(backfillResult.api.periods_written || []).length} period(s)
                                {backfillResult.api.action_counts &&
                                  Object.keys(backfillResult.api.action_counts).length > 0 && (
                                    <>
                                      {' '}&mdash;{' '}
                                      {Object.entries(backfillResult.api.action_counts)
                                        .map(([k, v]) => `${v} ${k}`)
                                        .join(', ')}
                                    </>
                                  )}
                                {(backfillResult.api.errors || []).length > 0 && (
                                  <span className="ms-1 text-warning">
                                    ({backfillResult.api.errors.join('; ')})
                                  </span>
                                )}
                              </>
                            ) : (
                              (backfillResult.api.errors || []).join('; ') || 'Failed'
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <hr className="my-3" />
                    <h6 className="fw-semibold mb-1">
                      <i className="bi bi-file-earmark-text me-2 text-primary" />
                      Daily SEC Filing Polling
                    </h6>
                    <p className="small text-muted mb-2">
                      When enabled, a daily job checks SEC EDGAR for new 10-K/10-Q/8-K
                      filings for this company, saves them (same pipeline as the SEC Filing
                      Manager's backfill), and automatically refreshes earnings from the
                      SEC/FMP APIs when a new filing is found.
                    </p>

                    <div className="form-check form-switch mb-2">
                      <input
                        type="checkbox"
                        role="switch"
                        id="cfg-sec-polling-enabled"
                        className="form-check-input"
                        checked={secPollingEnabled}
                        onChange={(e) => setSecPollingEnabled(e.target.checked)}
                        disabled={!ticker.trim()}
                      />
                      <label className="form-check-label small" htmlFor="cfg-sec-polling-enabled">
                        Enable daily SEC filing polling
                      </label>
                    </div>
                    {!ticker.trim() && (
                      <div className="form-text mb-2">
                        Set a ticker above to enable SEC filing polling.
                      </div>
                    )}

                    <div className="d-flex align-items-center gap-3 mb-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={handleSecPollNow}
                        disabled={secPolling || !secPollingEnabled || !ticker.trim()}
                        title={
                          secPollingEnabled
                            ? 'Check SEC EDGAR for new filings right now'
                            : 'Enable and save SEC filing polling above first'
                        }
                      >
                        {secPolling ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" />
                            Polling…
                          </>
                        ) : (
                          <>
                            <i className="bi bi-search me-1" />
                            Poll SEC filings now
                          </>
                        )}
                      </button>
                      {secLastPollTime && (
                        <span className="small text-muted">
                          Last polled: {new Date(secLastPollTime).toLocaleString()}
                        </span>
                      )}
                    </div>

                    {secPollError && (
                      <div className="alert alert-danger py-1 px-2 mb-0 small">{secPollError}</div>
                    )}

                    {secPollResult && (
                      <div
                        className={`alert py-1 px-2 mb-0 small ${
                          secPollResult.success ? 'alert-info' : 'alert-warning'
                        }`}
                      >
                        <strong>SEC Poll:</strong>{' '}
                        Found {(secPollResult.filings_found || []).length} new filing(s)
                        {secPollResult.earnings_refresh_triggered && (
                          <>
                            {' '}— earnings refresh{' '}
                            {secPollResult.earnings_refresh_result?.success ? 'completed' : 'attempted'}
                          </>
                        )}
                      </div>
                    )}

                    <hr className="my-3" />
                    <h6 className="fw-semibold mb-1">
                      <i className="bi bi-youtube me-2 text-danger" />
                      YouTube Transcript Backfill
                    </h6>
                    <p className="small text-muted mb-2">
                      Pull historical videos from this channel and store transcripts in
                      hff-research transcripts/ for KB ingestion.
                    </p>

                    <div className="d-flex flex-wrap align-items-end gap-3 mb-2">
                      <div>
                        <label className="form-label small mb-1 d-block">How far back?</label>
                        <div className="input-group input-group-sm" style={{ width: 190 }}>
                          <input
                            type="number"
                            min={1}
                            max={20}
                            className="form-control"
                            value={youtubeBackfillYears}
                            onChange={(e) => setYoutubeBackfillYears(e.target.value)}
                            disabled={youtubeBackfilling}
                          />
                          <span className="input-group-text">years</span>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={handleYoutubeBackfill}
                        disabled={youtubeBackfilling || !youtubeChannelId.trim()}
                        title={
                          youtubeChannelId.trim()
                            ? 'Backfill transcripts for historical videos on this channel'
                            : 'Set a YouTube Channel ID above to enable transcript backfill'
                        }
                      >
                        {youtubeBackfilling ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" />
                            Backfilling…
                          </>
                        ) : (
                          <>
                            <i className="bi bi-cloud-download me-1" />
                            Backfill transcripts
                          </>
                        )}
                      </button>
                    </div>

                    {youtubeBackfillError && (
                      <div className="alert alert-danger py-1 px-2 mb-0 small">
                        {youtubeBackfillError}
                      </div>
                    )}

                    {youtubeBackfillResult && (
                      <div className="alert alert-info py-1 px-2 mb-0 small">
                        <strong>YouTube:</strong>{' '}
                        Found {youtubeBackfillResult.videos_found || 0} historical video(s),
                        queued {youtubeBackfillResult.queued_videos_count ?? youtubeBackfillResult.queued ?? 0} for transcription,
                        skipped {youtubeBackfillResult.skipped_existing || 0} existing transcript(s)
                        {(youtubeBackfillResult.errors || []).length > 0 && (
                          <span className="text-danger">
                            {' '}
                            — {(youtubeBackfillResult.errors || []).length} error(s)
                          </span>
                        )}
                      </div>
                    )}

                    <hr className="my-3" />
                    <h6 className="fw-semibold mb-1">
                      <i className="bi bi-twitter-x me-2" />
                      X Post Backfill
                    </h6>
                    <p className="small text-muted mb-2">
                      Pull historical posts from this company's Official &amp; Trusted X
                      accounts and archive them (batched by day) in hff-research x-posts/
                      for KB ingestion. Accounts keep being monitored for new posts going forward.
                    </p>

                    <div className="d-flex flex-wrap align-items-end gap-3 mb-2">
                      <div>
                        <label className="form-label small mb-1 d-block">How far back?</label>
                        <div className="input-group input-group-sm" style={{ width: 190 }}>
                          <input
                            type="number"
                            min={1}
                            max={20}
                            className="form-control"
                            value={xBackfillYears}
                            onChange={(e) => setXBackfillYears(e.target.value)}
                            disabled={xBackfilling}
                          />
                          <span className="input-group-text">years</span>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="btn btn-sm btn-dark"
                        onClick={handleXBackfill}
                        disabled={
                          xBackfilling ||
                          (!xOfficialAccounts.trim() && !xTrustedAccounts.trim())
                        }
                        title={
                          xOfficialAccounts.trim() || xTrustedAccounts.trim()
                            ? 'Backfill historical posts for the Official/Trusted X accounts above'
                            : 'Add an Official or Trusted X account above to enable post backfill'
                        }
                      >
                        {xBackfilling ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" />
                            Backfilling…
                          </>
                        ) : (
                          <>
                            <i className="bi bi-cloud-download me-1" />
                            Backfill posts
                          </>
                        )}
                      </button>
                    </div>

                    {xBackfillError && (
                      <div className="alert alert-danger py-1 px-2 mb-0 small">
                        {xBackfillError}
                      </div>
                    )}

                    {xBackfillResult && (
                      <div className="alert alert-info py-1 px-2 mb-0 small">
                        <strong>X:</strong>{' '}
                        Queued {xBackfillResult.queued || 0} of{' '}
                        {xBackfillResult.accounts_found || 0} account(s) for backfill
                        {(xBackfillResult.errors || []).length > 0 && (
                          <span className="text-danger">
                            {' '}
                            — {(xBackfillResult.errors || []).length} error(s)
                          </span>
                        )}
                      </div>
                    )}

                  </div>
                </>
              )}

              {saveError && (
                <div className="alert alert-danger mt-3 mb-0">{saveError}</div>
              )}
              {saveWarning && (
                <div className="alert alert-warning mt-3 mb-0">{saveWarning}</div>
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving || loadingConfig}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" />
                    Saving…
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg me-1" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showSecPicker && (
        <SecMetricPickerDialog
          companyId={company?.company_id}
          existingKeys={metrics.map((m) => m.key)}
          onAdd={(m) => {
            setMetrics((prev) => {
              // Skip if a metric with this key already exists (idempotent add)
              if (prev.some((x) => x.key === m.key)) return prev;
              return [...prev, { ...m, _id: mkId() }];
            });
          }}
          onClose={() => setShowSecPicker(false)}
        />
      )}
    </>
  );
}
